import { useMemo } from 'react';
import { RuleApiModel, SmartSendItemApiModel } from '~/lib/api/smartsend/types';
import { createNewInlineRule, ruleMatchesActionsAndConditions } from '../../rules/RuleGenerator';
import { generateActionsForInlineRule } from './generateActionsForInlineRule';
import { generateConditionsForInlineRule } from './generateConditionsForInlineRule';
import { AllowedInlineConditions } from './AllowedInlineConditions';
import RuleItem from './RuleItem';
import RuleActionRuleGroup from './RuleActionRuleGroup';

interface props {
  item: SmartSendItemApiModel;
  actions: string[];
  rules: RuleApiModel[];
  addNewRule: (r: RuleApiModel) => void;
  updateExistingRule: (r: RuleApiModel) => void;
  toggle: (open: boolean) => void;
}

export default function RuleActionGroup(props: props) {
  const rules = useMemo(
    () => props.rules.filter((r) => ruleMatchesActionsAndConditions(r, props.item, props.actions)),
    [props.item, props.actions, props.rules]
  );

  const addRule = (conditions: string[], title: string, description: string) => {
    props.addNewRule(
      createNewInlineRule(
        title,
        description,
        generateConditionsForInlineRule(props.item, conditions),
        generateActionsForInlineRule(props.item, props.actions)
      )
    );
  };

  const updateExistingRule = (rule: RuleApiModel, conditions: string[], title: string, description: string) => {
    const updatedRule = { ...rule, Name: title, Description: description };
    updatedRule.Conditions = updatedRule.Conditions.filter((x) => !AllowedInlineConditions.includes(x.Field)).concat(
      generateConditionsForInlineRule(props.item, conditions)
    );
    props.updateExistingRule(updatedRule);
  };

  if (rules == null) {
    return null;
  }

  // Show rule selector if we have multiple rules that match
  if (rules.length > 1) {
    return (
      <RuleActionRuleGroup
        rules={rules}
        actions={props.actions}
        updateExistingRule={updateExistingRule}
        item={props.item}
        toggle={props.toggle}
      />
    );
  }

  return (
    <RuleItem
      showRuleName={false}
      actions={props.actions}
      addNewRule={addRule}
      item={props.item}
      onSelection={props.toggle}
      updateExistingRule={updateExistingRule}
      rule={rules.length === 1 ? rules[0] : null}
    />
  );
}
